import capitalize from 'lodash/capitalize';
import isEqual from 'lodash/isEqual';
import startCase from 'lodash/startCase';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import { DashboardAPI, LibraryAPI } from '@/apis';
import { axios } from '@/apis/dashboards';
import * as InstagramAPI from '@/apis/instagram';
import {
  createSegmentClusterTag,
  createSegmentClusterTagAssociations,
  getCompetitorTotalsByBrand,
  getSegmentClusterTags,
  removeSegmentClusterTag as deleteSegmentClusterTag,
  removeTagFromAllSegmentClusters,
} from '@/apis/library';
import * as TwitterAPI from '@/apis/twitter';
import constants, { TOTAL_ENGAGEMENTS_METRIC } from '@/app/competitive/constants';
import { formatSegmentCluster } from '@/app/competitive/utils';
import { parseIndustryName } from '@/app/dashboards/utils/benchmarks.utils';
import { INDUSTRY_FOLLOWER_BUCKETS } from '@/models/dashboards/benchmark-type.enum';
import { CHANNELS } from '@/models/dashboards/channels.enum';
import { useAuthStore } from '@/stores/auth';
import { useNotificationStore } from '@/stores/notification';

const CANCELED_ERROR_NAME = 'CanceledError';

export const useCompetitiveStore = defineStore('competitive', () => {
  function buildBenchmarkKey(id, followerBucket, metric) {
    const benchmarkId = id || 'all_industries';
    return `benchmarks_${benchmarkId}_${followerBucket?.toLowerCase()}_${metric?.toLowerCase()}`;
  }

  const pending = ref({
    barChartData: false,
    brandEngagements: false,
    competitiveSegments: true,
    competitorMedia: false,
    industries: true,
    addCompetitor: false,
    competitorTotalsByBrand: false,
    removeTagFromAllCompetitors: false,
    addTagsToMultipleCompetitors: false,
  });

  const error = ref({
    barChartData: false,
    brandEngagements: false,
    competitiveSegments: [],
    competitorMedia: [],
    industries: [],
    addCompetitor: [],
    competitorTotalsByBrand: [],
  });

  const showCompetitorPopup = ref(false);
  const barChartData = ref({});
  const competitiveSegments = ref([]);
  const activeCompetitorTags = ref([]);
  const competitorTags = ref([]);
  const competitorMedia = ref([]);
  const competitorMediaNextUrl = ref(null);
  const competitorTotalsByBrand = ref({});
  const fetchAccountsErrorMessage = ref(null);
  const industries = ref([]);
  const selectedGraphMetric = ref(null);
  const socialMediaType = ref(null);
  const brandEngagementsData = ref(null);

  let brandEngagementsController = null;

  const authStore = useAuthStore();
  const notificationStore = useNotificationStore();

  let barChartDataController = null;
  let competitorMediaController = null;
  let competitiveSegmentsController = null;

  const overLimit = computed(() => {
    const channelTotals =
      competitorTotalsByBrand.value?.[socialMediaType.value?.toUpperCase()] ?? 0;
    const formattedChannelName = socialMediaType.value?.toLowerCase();
    const competitorKey = `${formattedChannelName}_competitor_limit`;
    const limit = authStore.currentBrand?.permissions?.[formattedChannelName]?.[competitorKey];
    return channelTotals >= limit;
  });

  const competitorBrandIds = computed(() =>
    competitiveSegments.value.map((competitor) => competitor.targetSourceId),
  );
  const competitorBrandHandlesJoinedLabel = computed(() =>
    competitiveSegments.value
      .map((competitor) => startCase(capitalize(formatSegmentCluster(competitor).handle)))
      .join(','),
  );

  const competitorFilters = ref([
    {
      field: 'competitors',
      operator: 'is',
      value: [],
      label: '',
    },
  ]);
  const graphMetric = computed(() => {
    if (selectedGraphMetric.value) {
      return selectedGraphMetric.value;
    }
    const metrics = constants.competitiveOverviewChartConfig[socialMediaType?.value]?.metrics ?? [];
    return metrics.find((metric) => metric.default) || metrics[0];
  });

  const benchmarkOptions = computed(() => {
    const options = [constants.defaultSelectedBenchmark, ...industries.value];
    return options.map((industry) => ({
      value: buildBenchmarkKey(industry.id, industry.follower_bucket, graphMetric?.value?.value),
      label: parseIndustryName(
        industry.name,
        INDUSTRY_FOLLOWER_BUCKETS[industry.follower_bucket].text,
        industry.dashboard_follower_buckets,
      ),
      id: industry.id,
      follower_bucket: industry.follower_bucket,
    }));
  });

  const graphBenchmark = ref(benchmarkOptions.value[0]);

  watch(competitorBrandIds, (to, from) => {
    if (!isEqual(to, from)) {
      competitorFilters.value =
        competitorBrandIds.value.length > 0
          ? [
              {
                field: 'competitors',
                operator: 'is',
                value: competitorBrandIds.value,
                label: competitorBrandHandlesJoinedLabel,
              },
            ]
          : [
              {
                field: 'competitors',
                operator: 'is',
                value: [],
                label: '',
              },
            ];
    }
  });

  watch(socialMediaType, (to, from) => {
    if (to === from) {
      return;
    }
    const metrics = constants.competitiveOverviewChartConfig[to].metrics;
    selectedGraphMetric.value = metrics.find((metric) => metric.default) || metrics[0];
    graphBenchmark.value = benchmarkOptions.value[0];
  });

  function clearCompetitiveSegments() {
    competitiveSegments.value = [];
  }

  async function fetchCompetitiveSegments({
    brandId,
    tagId,
    sources,
    isUgc,
    period,
    mediaCount,
    search,
    tags,
    tagsFilterType,
    sort,
    url,
    summary,
  }) {
    if (competitiveSegmentsController) {
      competitiveSegmentsController.abort();
    }
    competitiveSegmentsController = new AbortController();
    error.value.competitiveSegments = null;
    pending.value.competitiveSegments = true;
    try {
      const response = await LibraryAPI.getSegmentClusters(
        {
          brandId,
          tagId,
          sources,
          isUgc,
          period,
          mediaCount,
          search,
          tags,
          tagsFilterType,
          sort,
          url,
          summary,
        },
        { signal: competitiveSegmentsController.signal },
      );
      const payload = response?.data;
      if (payload) {
        competitiveSegments.value = payload
          .map((competitor) => ({
            ...competitor,
            formattedData: formatSegmentCluster(competitor),
            tags: competitor.tags?.reverse() ?? [],
          }))
          .filter((competitor) => competitor.formattedData.handle !== authStore.currentBrand?.name);
      }
      pending.value.competitiveSegments = false;
    } catch (err) {
      if (err?.response?.status === 404) {
        competitiveSegments.value = [];
        pending.value.competitiveSegments = false;
      } else if (err?.name === CANCELED_ERROR_NAME) {
        pending.value.competitiveSegments = true;
      } else {
        error.value.competitiveSegments = err;
        pending.value.competitiveSegments = false;
        throw err;
      }
    }
  }

  async function fetchActiveCompetitorTags() {
    const response = await getSegmentClusterTags({
      brandId: authStore.currentBrand?.id,
      activeOnly: true,
    });
    activeCompetitorTags.value = response.data;
  }

  async function fetchAllCompetitorTags() {
    const response = await getSegmentClusterTags({ brandId: authStore.currentBrand?.id });
    competitorTags.value = response.data;
  }

  async function removeTagFromAllCompetitors({ brandId, tagId }) {
    pending.value.removeTagFromAllCompetitors = true;
    try {
      await removeTagFromAllSegmentClusters({ brandId, tagId });
    } catch (err) {
      if (err.response?.status !== 404) {
        throw err;
      }
    } finally {
      pending.value.removeTagFromAllCompetitors = false;
    }
  }

  function clearCompetitorMedia() {
    competitorMediaNextUrl.value = null;
    competitorMedia.value = [];
  }

  async function createCompetitorTag({ name, color }) {
    try {
      const response = await createSegmentClusterTag({
        brandId: authStore.currentBrand?.id,
        name,
        color,
      });
      const newTag = response?.data;
      activeCompetitorTags.value.push(newTag);
      competitorTags.value.push(newTag);
      return newTag;
    } catch (err) {
      if (err.response?.status === 400) {
        notificationStore.setToast({
          message: `A tag with the name "${name}" has already been added.`,
          type: 'warning',
        });
      } else {
        notificationStore.setToast({
          message: 'An error occurred while trying to create the tag.',
          type: 'error',
        });
      }
      return null;
    }
  }

  async function addTagsToMultipleCompetitors(segmentClusterIds, segmentClusterTagIds) {
    pending.value.addTagsToMultipleCompetitors = true;
    try {
      const suffix = segmentClusterIds.length > 1 ? 'competitors' : 'competitor';
      await createSegmentClusterTagAssociations({
        brandId: authStore.currentBrand?.id,
        segmentClusterIds,
        segmentClusterTagIds,
      });
      notificationStore.setToast({
        message: `Your tags have been added to ${segmentClusterIds.length} ${suffix}.`,
      });
      return true;
    } catch {
      notificationStore.setToast({
        message: 'An error occurred while trying to add the tags to competitors selected.',
        type: 'error',
      });
      return false;
    } finally {
      pending.value.addTagsToMultipleCompetitors = false;
    }
  }

  async function removeSegmentClusterTag(segmentClusterId, tagId) {
    try {
      await deleteSegmentClusterTag({
        brandId: authStore.currentBrand?.id,
        clusterId: segmentClusterId,
        tagId,
      });

      // remove tag from competitor in store for reactivity
      competitiveSegments.value?.forEach((competitor) => {
        if (competitor.id === segmentClusterId) {
          competitor.tags = competitor.tags.filter((competitorTag) => competitorTag.id !== tagId);
        }
      });

      notificationStore.setToast({
        message: 'Tag has been removed from competitor.',
      });
      return true;
    } catch {
      notificationStore.setToast({
        message: 'An error occurred while trying to remove the tag, please try again.',
        type: 'error',
      });
      return false;
    }
  }

  async function fetchCompetitorMedia({
    brandId,
    limit,
    sortFields,
    filters,
    url,
    includeCreator,
    clearMedia = false,
  }) {
    if (competitorMediaController) {
      competitorMediaController.abort();
    }
    competitorMediaController = new AbortController();
    error.value.competitorMedia = null;
    pending.value.competitorMedia = true;
    try {
      const response = await LibraryAPI.getMediaListV2(
        {
          brandId,
          limit,
          sortFields,
          filters,
          url,
          includeCreator,
        },
        { signal: competitorMediaController.signal },
      );
      const payload = response?.data;
      if (clearMedia) {
        clearCompetitorMedia();
      }
      if (payload?.paging) {
        competitorMediaNextUrl.value = payload.paging.next;
      }
      if (payload?.data) {
        this.competitorMedia = [...this.competitorMedia, ...payload.data];
      }
      return true;
    } catch (err) {
      if (err?.response?.status === 404) {
        competitorMedia.value = [];
      } else if (err?.name === CANCELED_ERROR_NAME) {
        return err;
      } else {
        error.value.competitorMedia = err;
        throw err;
      }
      return err;
    } finally {
      pending.value.competitorMedia = false;
    }
  }

  async function fetchIndustries(includeFollowerBuckets = true) {
    try {
      const response = await DashboardAPI.getIndustries(includeFollowerBuckets);
      const payload = response?.data;
      if (payload.length) {
        this.industries = [...payload];
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        industries.value = [];
      } else {
        error.value.industries = err;
        throw err;
      }
    } finally {
      pending.value.industries = false;
    }
  }

  async function fetchBarChartData(params, body) {
    if (barChartDataController) {
      barChartDataController.abort();
    }
    barChartDataController = new AbortController();

    pending.value.barChartData = true;
    const queryParams = new URLSearchParams(params).toString();
    try {
      const response = await axios.put(`/reports/data?${queryParams}`, body, {
        signal: barChartDataController.signal,
      });
      const payload = response?.data;
      if (payload?.data) {
        this.barChartData = payload.data;
      }
      pending.value.barChartData = false;
      return true;
    } catch (err) {
      error.value.barChartData = err;
      pending.value.barChartData = false;
      return err;
    }
  }
  // TODO [SC: 181025] revert this PR once we have a brand metric that can calculate this
  //  and support it in dashboard backend
  async function fetchBrandEngagements({ brandId, startDate, endDate }) {
    if (brandEngagementsController) {
      brandEngagementsController.abort();
    }
    brandEngagementsController = new AbortController();

    pending.value.brandEngagements = true;
    error.value.brandEngagements = false;
    brandEngagementsData.value = null;

    try {
      const likesParams = {
        aggregate_by: 'TOTAL',
        brand_ids: brandId,
        channels: 'INSTAGRAM',
        metrics: 'ORGANIC_LIKES',
        start_date: startDate,
        end_date: endDate,
        context_start_date: startDate,
        context_end_date: endDate,
      };

      const commentsParams = {
        aggregate_by: 'TOTAL',
        brand_ids: brandId,
        channels: 'INSTAGRAM',
        metrics: 'ORGANIC_COMMENTS',
        start_date: startDate,
        end_date: endDate,
        context_start_date: startDate,
        context_end_date: endDate,
      };

      const likesQueryParams = new URLSearchParams(likesParams).toString();
      const commentsQueryParams = new URLSearchParams(commentsParams).toString();

      const [likesRes, commentsRes] = await Promise.all([
        axios.put(
          `/reports/data?${likesQueryParams}`,
          {},
          {
            signal: brandEngagementsController.signal,
          },
        ),
        axios.put(
          `/reports/data?${commentsQueryParams}`,
          {},
          {
            signal: brandEngagementsController.signal,
          },
        ),
      ]);

      const likesData = likesRes?.data?.data;
      const commentsData = commentsRes?.data?.data;

      if (!likesData || !commentsData) {
        pending.value.brandEngagements = false;
        return true;
      }

      const brandKey = Object.keys(likesData).find((key) => likesData[key]?.data_type === 'BRAND');

      if (!brandKey) {
        pending.value.brandEngagements = false;
        return true;
      }

      const brandLikesEntry = likesData[brandKey];
      const brandCommentsEntry = commentsData[brandKey];

      const likesValue = brandLikesEntry?.metrics?.ORGANIC_LIKES?.INSTAGRAM?.value ?? 0;
      const commentsValue = brandCommentsEntry?.metrics?.ORGANIC_COMMENTS?.INSTAGRAM?.value ?? 0;

      brandEngagementsData.value = {
        id: brandKey,
        avatar: brandLikesEntry.avatar,
        name: brandLikesEntry.name,
        data_type: 'BRAND',
        metrics: {
          [TOTAL_ENGAGEMENTS_METRIC]: {
            INSTAGRAM_COMPETITIVE: {
              value: likesValue + commentsValue,
            },
          },
        },
      };

      pending.value.brandEngagements = false;
      return true;
    } catch (err) {
      if (err?.name === CANCELED_ERROR_NAME) {
        pending.value.brandEngagements = true;
        return err;
      }
      error.value.brandEngagements = err;
      pending.value.brandEngagements = false;
      return err;
    }
  }

  async function fetchAccounts({ brandId, source, handle }) {
    let accounts;
    pending.value.accounts = true;
    error.value.accounts = null;
    fetchAccountsErrorMessage.value = null;
    try {
      const response = await LibraryAPI.getAutocomplete({ brandId, source, handle });
      const payload = response?.data;
      if (payload) {
        accounts = payload.filter(
          (account) =>
            !competitiveSegments.value
              .map((segment) => segment.target_source_id)
              .includes(account.name),
        );
      }
      pending.value.accounts = false;
      return accounts;
    } catch (err) {
      error.value.accounts = err;
      pending.value.accounts = false;
      if (err.response?.status === 403 && source === CHANNELS.YOUTUBE.value) {
        fetchAccountsErrorMessage.value = constants.quotaLimitErrorMessage;
      }
      return [];
    }
  }

  async function fetchCompetitorTotalsByBrand({ brandId }) {
    pending.value.competitorTotalsByBrand = true;
    try {
      const response = await getCompetitorTotalsByBrand({ brandId });
      const payload = response?.data;
      if (payload) {
        competitorTotalsByBrand.value = payload;
      }
    } catch (err) {
      error.value.competitorTotalsByBrand = err;
    } finally {
      pending.value.competitorTotalsByBrand = false;
    }
  }

  async function addCompetitor({ brandId, targetSource, targetSourceId, name }) {
    pending.value.addCompetitor = true;
    try {
      if (targetSource === CHANNELS.INSTAGRAM.value) {
        await InstagramAPI.addAccount({ handle: name });
      } else if (targetSource === CHANNELS.TWITTER.value) {
        await TwitterAPI.addAccount({ handle: name });
      }

      const response = await LibraryAPI.createSegmentCluster({
        brandId,
        targetSource,
        targetSourceId,
      });
      return response.data;
    } catch (err) {
      error.value.addCompetitor = err;
      pending.value.addCompetitor = false;
      throw err;
    }
  }

  return {
    activeCompetitorTags,
    barChartData,
    barChartDataController,
    benchmarkOptions,
    brandEngagementsData,
    buildBenchmarkKey,
    clearCompetitorMedia,
    clearCompetitiveSegments,
    competitorBrandIds,
    competitorBrandHandlesJoinedLabel,
    competitorFilters,
    competitorMedia,
    competitorMediaNextUrl,
    competitiveSegments,
    competitorTotalsByBrand,
    competitorTags,
    error,
    fetchBarChartData,
    fetchBrandEngagements,
    fetchCompetitorMedia,
    fetchCompetitiveSegments,
    fetchActiveCompetitorTags,
    fetchAllCompetitorTags,
    createCompetitorTag,
    addTagsToMultipleCompetitors,
    fetchCompetitorTotalsByBrand,
    fetchIndustries,
    fetchAccounts,
    addCompetitor,
    graphBenchmark,
    graphMetric,
    industries,
    pending,
    selectedGraphMetric,
    socialMediaType,
    showCompetitorPopup,
    fetchAccountsErrorMessage,
    removeTagFromAllCompetitors,
    removeSegmentClusterTag,
    overLimit,
  };
});
